// @flow
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, Box, Divider, IconButton} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import {useForm} from 'react-hook-form';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import withoutImage from '../../../../assets/img/withoutImage.png';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {getImageUrl} from '../../../../utils/image_provider';

import type {CategoryRequest} from '../../api';
import type {Category} from '../../../../entities';

import CropModal from '../../../../elements/CropModal';
import type {ImageToCrop} from '../../../../elements/CropModal';
type Props = {
  open: boolean,
  isSaving?: boolean,
  handleClose: () => void,
  createCategory: ({categoryRequest: CategoryRequest}) => void,
  editCategory: ({categoryRequest: CategoryRequest}) => void,
  category?: Category,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2, 0),
  },
  content: {
    maxWidth: 500,
    width: '100%',
    margin: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  subtitle: {
    textAlign: 'start',
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionTitle: {
    fontWeight: 600,
    textAlign: 'start',
  },
  row: {
    marginTop: theme.spacing(1),
    display: 'flex',
  },
  categoryTextField: {
    flexGrow: 1,
  },
  categoryButton: {
    width: '100%',
    borderRadius: '32px',
    backgroundColor: theme.palette.primary.main,
  },
  inputFileWrapper: {
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  labelAddImage: {
    borderRadius: 8,
    boxSizing: 'border-box',
    width: '100%',
    margin: '8px auto 12px',
  },
  inputFile: {
    position: 'absolute !important',
    height: 1,
    width: 1,
    overflow: 'hidden',
    clip: 'rect(1px, 1px, 1px, 1px)',
  },
  text: {
    color: '#6A6A7F',
  },
  cropContainer: {
    position: 'relative',
  },
  refreshButton: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
  textTooltip: {
    textAlign: 'left!important',
    color: ' #717190',
    fontSize: '10px',
    '& span': {
      marginLeft: theme.spacing(1),
    },
  },
  imagePreview: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '.5rem',

    border: '1.5px dashed #B8B8C7',
    cursor: 'pointer',

    '& .preview': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    '&[data-direction="horizontal"]': {
      aspectRatio: 3.32,
    },

    '&[data-direction="vertical"]': {
      aspectRatio: 1,
    },
  },
  deleteButton: {
    background: 'white',
    color: 'black',
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 100,
    cursor: 'pointer',
    borderRadius: '50%',
    padding: 4,
  },
}));
const RECOMMENDED_SIZE = {
  vertical: {
    width: 600,
    height: 600,
  },
  horizontal: {
    width: 1200,
    height: 360,
  },
};
export default function CategoryModal(props: Props) {
  const {
    open,
    isSaving,
    handleClose,
    createCategory,
    editCategory,
    category,
  } = props;

  const classes = useStyles();
  const {register, handleSubmit, errors, reset, setValue, watch} = useForm();

  const watchAllValues = watch();

  const [imageToCrop, setImageToCrop] = useState<ImageToCrop | null>(null);

  const [
    verticalImageCropped,
    setVerticalImageCropped,
  ] = useState<ImageToCrop | null>(null);

  const [
    horizontalImageCropped,
    setHorizontalImageCropped,
  ] = useState<ImageToCrop | null>(null);

  const imageDirectionRef = useRef<string>('');

  const [verticalImageSaved, setVerticalImageSaved] = useState<string>(
    category && category.portraitImageId
      ? getImageUrl(category.portraitImageId)
      : ''
  );
  const [horizontalImageSaved, setHorizontalImageSaved] = useState<string>(
    category && category.landscapeImageId
      ? getImageUrl(category.landscapeImageId)
      : ''
  );

  const disabledButton = useMemo(() => {
    return isSaving || watchAllValues.newCategoryName === '';
  }, [isSaving, watchAllValues.newCategoryName]);

  const handleAddCategorySubmit = async data => {
    let categoryRequest = {
      name: data.newCategoryName,
      portraitImage: verticalImageCropped
        ? verticalImageCropped.file
        : undefined,
      landscapeImage: horizontalImageCropped
        ? horizontalImageCropped.file
        : undefined,
    };

    if (!category) {
      //CREATE
      if (data.newCategoryName)
        createCategory({
          categoryRequest,
        });
    } //EDIT
    else {
      categoryRequest = {
        ...categoryRequest,
        originalHorizontalFile: !!horizontalImageSaved,
        originalVerticalFile: !!verticalImageSaved,
        portraitImageId: category.portraitImageId,
        landscapeImageId: category.landscapeImageId,
      };
      editCategory({categoryRequest: {...categoryRequest, id: category.id}});
    }

    cleanLocalStates();
    handleClose();
  };

  useEffect(
    () => () => {
      reset();
      cleanLocalStates();
    },
    [reset]
  );

  const handleChangeInput = (e, direction: string) => {
    e.stopPropagation();
    const files = e.target.files;
    const file = files[0];
    if (!file) return;
    const imageUrl = URL.createObjectURL(files[0]);

    imageDirectionRef.current = direction;

    setImageToCrop({
      file,
      preview: imageUrl,
    });

    e.target.value = null;
  };

  useEffect(() => {
    if (category) setValue('newCategoryName', category.name);
  }, [category, setValue]);

  const cleanLocalStates = () => {
    setVerticalImageCropped(null);
    setHorizontalImageCropped(null);
    setVerticalImageSaved('');
    setHorizontalImageSaved('');
    setImageToCrop(null);
    imageDirectionRef.current = '';
  };
  const handleCloseModal = () => {
    cleanLocalStates();
    handleClose();
  };

  const verticalImagePreview = verticalImageCropped
    ? verticalImageCropped.preview
    : verticalImageSaved;

  const horizontalImagePreview = horizontalImageCropped
    ? horizontalImageCropped.preview
    : horizontalImageSaved;

  const handleRemoveImage = (direction: string) => {
    if (direction === 'vertical') {
      setVerticalImageCropped(null);
      setVerticalImageSaved('');
    } else {
      setHorizontalImageCropped(null);
      setHorizontalImageSaved('');
    }
  };
  const aspectRatio = imageDirectionRef.current === 'vertical' ? 1 : 3.32;
  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Box className={classes.content}>
          <Typography align="center" gutterBottom className={classes.title}>
            {category ? 'Editar Categoría' : 'Nueva Categoría'}
          </Typography>
          <Typography align="center" className={classes.subtitle}>
            Para mejorar el diseño de tu tienda, asegúrate de encuadrar los dos
            estilos: cuadrado y rectangular.
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <CustomTextField
              className={classes.categoryTextField}
              name="newCategoryName"
              error={!!errors.newCategoryName}
              defaultValue={category ? category.name : ''}
              helperText={
                errors.newCategoryName ? errors.newCategoryName.message : ''
              }
              inputRef={register({
                required: 'Campo obligatorio',
                pattern: {
                  value: /^\S.*$/,
                  message: 'Formato incorrecto',
                },
              })}
              type="text"
              disabled={isSaving}
              placeholder="Nombre de la categoría"
              id="category-name"
            />
          </div>
          <br />

          <div>
            <div className={classes.text} id="category-picture">
              1. Selecciona una imágen para corte cuadrado.
            </div>
            <div className={classes.cropContainer}>
              <SectionAddImage
                direction="vertical"
                preview={verticalImagePreview}
                onChange={e => handleChangeInput(e, 'vertical')}
              />
              {verticalImagePreview && (
                <IconButton
                  className={classes.deleteButton}
                  onClick={() => handleRemoveImage('vertical')}
                >
                  <Close />
                </IconButton>
              )}
            </div>
          </div>
          <br />
          <div>
            <div className={classes.text} id="category-cover-picture">
              2. Selecciona una imágen para corte rectangular.
            </div>
            <div className={classes.cropContainer}>
              <SectionAddImage
                direction="horizontal"
                preview={horizontalImagePreview}
                onChange={e => handleChangeInput(e, 'horizontal')}
              />
              {horizontalImagePreview && (
                <IconButton
                  className={classes.deleteButton}
                  onClick={() => handleRemoveImage('horizontal')}
                >
                  <Close />
                </IconButton>
              )}
            </div>
          </div>

          <br />
          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleAddCategorySubmit)}
            disabled={disabledButton}
            className={`${classes.categoryButton}`}
            id="save-category"
          >
            {category ? 'Guardar cambios' : 'Agregar categoría'}
          </CustomButton>
        </Box>
      </div>
      {imageToCrop && (
        <CropModal
          open
          images={[imageToCrop]}
          onClose={() => setImageToCrop(null)}
          aspect={aspectRatio}
          compressOptions={{
            quality: 0.8,
            maxWidth: 2400,
            maxHeight: 2400 / aspectRatio,
          }}
          onSubmit={results => {
            const [result] = results;
            const direction = imageDirectionRef.current;
            if (direction === 'vertical') {
              setVerticalImageCropped(result);
            } else {
              setHorizontalImageCropped(result);
            }
            setImageToCrop(null);
          }}
        />
      )}
    </CustomModal>
  );
}

function SectionAddImage({
  direction,
  preview,
  onChange,
}: {
  direction: string,
  preview?: string,
  onChange: (e: SyntheticInputEvent<HTMLInputElement>) => void,
}) {
  const classes = useStyles();
  function handleChange(e: SyntheticInputEvent<HTMLInputElement>) {
    onChange(e);
  }

  return (
    <div className={classes.inputFileWrapper}>
      <label className={classes.imagePreview} data-direction={direction}>
        <input
          className={classes.inputFile}
          hidden
          type="file"
          onChange={handleChange}
          accept="image/*"
        />
        <img
          src={preview || withoutImage}
          alt="category"
          className={preview ? 'preview' : ''}
        />
        {!preview && (
          <span>
            {`${RECOMMENDED_SIZE[direction].width} x ${RECOMMENDED_SIZE[direction].height}`}
          </span>
        )}
      </label>
    </div>
  );
}
